<template>
  <tr>
    <td>
      <preview
          :key="previewReRenderKey"
          :cover="true"
          :downloadable="false"
          :preview-id="`${instance.id}/${previewKey}`"
          :preview-uri="`${instance.id}/${previewKey}`"
          :removable="false"
          class="preview-image"
          source="Instance"
          @loadingFailed="setAltPreview"
      />
    </td>
    <td class="threedot-name-field">
      <three-dot-field
          class="preview-dot-field"
          min-width="600px"
          dot-icon="eye"
          open-direction="right"
      >
        <div class="mb-1">{{ instance.displayName }}</div>
        <filter-display
            class="mb-2"
            :content="JSON.parse(instance.content)"
        />
      </three-dot-field>
      <router-link
          class="d-inline-block lighter clickable float-left mr-1"
          :to="'/library/3d-data/instance/' + instance.id + '/files'"
          target="_blank"
          v-if="$store.getters.isSuperAdmin || $store.getters.isSFXMember">
        <icon type="angle-right" />
      </router-link>
      <name-display-edit-block
          :id="instance.projectId"
          :cid="instance.id"
          :display-name="instance.displayName"
          :name="instance.name"
          :organization-id="null"
          :check-for-unique-name="false"
          :edit-icon-size="0.8"
          html-tag-for-display="p"
          :icon="null"
          store-name="FilterInstance"
          @updated="updated"
      />
      <div class="lighter">{{ dateTimeFromISO(instance.updatedAt) }}</div>
    </td>
    <td>
      <div v-if="!projectUsage.length">
        -
      </div>
      <mini-popup v-else>
        <div slot="linkContent" class="clickable underline">
          {{ projectUsage.length }} <icon type="angle-right"/>
        </div>
        <div slot="popupContent">
          <b class="mb-1">{{ $t('filter.usedInProjects') }}</b>
          <div class="project-list">
            <div v-for="profile in projectUsage" :key="profile.id">
              <router-link
                  :to="'/project/' + profile.projectId + '/general'"
                  target="_blank"
              >
                <icon type="folder" /> {{ profile.projectName }}
              </router-link>
            </div>
          </div>
        </div>
      </mini-popup>
    </td>
    <td class="text-center">
      <div v-if="instance.state === 'a'">
        <download-button
            :div-instead-of-button="true"
            :object-id="instance.id"
            :show-stats="false"
            class="text-center w-100 not-absolute mb-1 clickable"
            download-message=""
            loading-panel-classes="black"
        >
          <icon size="1.2" type="download"/>
        </download-button>
      </div>
      <div v-else>
        <icon class="lighter" size="1.3" type="clock"/>
      </div>
    </td>
    <td>
      <icon v-if="outOfSync" type="exclamation-triangle"/>
      <icon v-else type="check"/>
    </td>
    <td class="last-profile-instance-field">
      <popup
          v-if="mockupPreviewDialog"
          @close="closeMockupPreviewDialog"
      >
        <div slot="popupMainContent">
          <h5 class="mb-3">{{ $t('vSTAGE Mockup Preview') }}</h5>
          <p class="lighter smaller mb-3">{{ $t('Select a file from this instance to use as the mockup preview link.') }}</p>
          <loading-spinner v-if="mockupPreviewLoading" class="mb-3"/>
          <div v-else>
            <select v-model="mockupPreviewSelectedKey" class="custom-select form-text-dark darker w-100 mb-3 mockup-preview-select">
              <option v-if="!mockupPreviewFiles.length" value="" disabled>{{ $t('No files found') }}</option>
              <option v-for="file in mockupPreviewFiles" :key="file.key" :value="file.key">
                {{ file.key }}
              </option>
            </select>
            <div class="d-flex mt-2">
              <div class="settings-button mr-2" @click="closeMockupPreviewDialog">{{ $t('cancel') }}</div>
              <div
                  :class="['settings-button', !mockupPreviewSelectedKey ? 'disabled' : '']"
                  @click="confirmMockupPreview"
              >
                {{ $t('confirm') }}
              </div>
            </div>
          </div>
        </div>
      </popup>
      <three-dot-field
          class="elevated-z-index"
      >
        <div class="lighter">ID: {{ instance.id }}</div><br >
        <div
            v-if="instance.state === 'a' || true"
            @click="openMockupPreviewDialog"
            class="settings-button d-inline-block switched-colors mt-1">
          {{ $t('Use as vSTAGE Mockup Preview') }} <icon class="ml-1" size="0.9" type="link"/>
        </div>
        <div
            @click="deleteInstance"
            class="settings-button d-inline-block switched-colors mt-1">
          {{ $t('delete') }} <icon class="ml-1" size="0.9" type="trash"/>
        </div>
      </three-dot-field>
      <pipeline-compact-state-viewer
          v-if="isFullyLoaded"
          :item="instance"
      />
      <loading-spinner v-else size="small" class="mt-2"/>
      <br/>
      <div class="lighter">{{ $t('filter.totalTime') }} {{ newestVMLogDuration ? newestVMLogDuration : '-' }}</div>
      <div @click="showHistory = true;"
           v-if="$store.getters.isSuperAdmin"
           class="settings-button d-inline-block mt-1 switched-colors">
        Show log history
      </div>
      <popup
          v-if="showHistory"
          :close-on-escape="true"
          :close-on-click-outside="true"
          @close="showHistory = false"
      >
      <pipeline-history-viewer
          slot="popupMainContent"
          class="exportprofile-instance-history-viewer mt-2"
          :task-entries="instance.vmLogs ? instance.vmLogs : []"
      />
      </popup>
    </td>
  </tr>
</template>

<script>
import Icon from "@/components/Icon.vue";
import DateMixin from "@/components/mixins/DateMixin.js.vue";
import Preview from "@/components/preview/Preview";
import DownloadButton from "@/components/forms/DownloadButton.vue";
import MiniPopup from "@/components/exportProfiles/MiniPopup.vue";
import ThreeDotField from "@/components/exportProfiles/ThreeDotField.vue";
import PipelineCompactStateViewer from "@/components/pipeline/PipelineCompactStateViewer.vue";
import vmLogEntry from "@/components/pipeline/vmLogEntry";
import NameDisplayEditBlock from "@/components/NameDisplayEditBlock.vue";
import FilterDisplay from "@/components/exportProfiles/FilterDisplay.vue";
import PipelineHistoryViewer from "@/components/pipeline/PipelineHistoryViewer.vue";
import Popup from "@/components/Popup.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
export default {
  name: "ExportProfileInstance",
  components: {
    PipelineHistoryViewer,
    FilterDisplay,
    NameDisplayEditBlock,
    DownloadButton,
    Icon,
    Preview,
    MiniPopup,
    ThreeDotField,
    PipelineCompactStateViewer,
    Popup,
    LoadingSpinner
  },
  mixins: [DateMixin],
  props: {
    instance: {type: Object, required: true},
    parentContent: {type: String, required: true},
    linkedExportProfiles: {type: Array, required: false, default: () => []},
  },
  data() {
    return {
      previewKey: 'preview.png',
      altPreviewKey: 'lod0_preview.png',
      listFiles: [],
      previewReRenderKey: 0,
      editName: false,
      localInstance: null,
      showHistory: false,
      isFullyLoaded: false,
      mockupPreviewDialog: false,
      mockupPreviewFiles: [],
      mockupPreviewSelectedKey: '',
      mockupPreviewLoading: false,
    }
  },
  computed: {
    instanceVMLogs() {
      return this.instance.vmLogs || [];
    },
    newestVMLogDuration() {
      if(!this.instanceVMLogs.length) {
        return '';
      }
      const logs = this.instanceVMLogs;
      const sortedLogs = logs.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      const logEntry = new vmLogEntry(sortedLogs[0]);
      return logEntry.getTotalTime();
    },
    projectUsage() {
      return this.linkedExportProfiles.filter(profile => profile.exportProfileInstanceId === this.instance.id);
    },
    outOfSync() {
      const parsedParent = JSON.parse(this.parentContent);
      const parsedInstance = JSON.parse(this.instance.content);
      const comparingProperties = ['pipeline', 'mode', 'filter', 'filterStringArray', 'merge', 'mergeStringArray', 'filterIdArray', 'mergeIdArray'];
      for (const prop of comparingProperties) {
        if(!this.propIsEqual(parsedParent[prop], parsedInstance[prop])) {
          return true;
        }
      }
      return false;
    },
  },
  beforeMount() {
    this.init();
  },
  methods: {
    propIsEqual(prop1, prop2) {
      // if one is undefined and the other an empty array, consider them equal
      if ((prop1 === undefined && Array.isArray(prop2) && prop2.length === 0) ||
          (prop2 === undefined && Array.isArray(prop1) && prop1.length === 0)) {
        return true;
      }
      return JSON.stringify(prop1) === JSON.stringify(prop2);
    },
    updated() {
      this.$emit('updated');
    },
    nameEdit() {
      this.editName = this.instance.displayName;
    },
    deleteInstance() {
      this.$emit('deleteInstance', this.instance.id);
    },
    taskIsRunning(taskManagerRow) {
      if(this.instance.state === 'a') {
        return false;
      } else if(this.instance.state === 'p') {
        return true;
      }
      if (!taskManagerRow) {
        return false;
      } else {
        const {prio} = taskManagerRow;
        return prio != 666;
      }
    },
    async loadVmLogsForInstance() {
      const logs = await this.$store.dispatch('clientLoadVMLogs', {
        filter: 'objectId eq ' + this.instance.id
      }).catch(() => {
        return [];
      });
      this.$set(this.instance, "vmLogs", logs);
    },
    async loadTaskManagerAndVMLogsAndInstance() {
      const instanceWithIncludes = await this.$store.dispatch('clientLoadProjectInstance', {
        id: this.instance.projectId,
        cid: this.instance.id,
        include: 'taskManagerRow,vmLogs',
      });
      const {taskManagerRows, vmLogs, state} = instanceWithIncludes;
      this.$set(this.instance, "taskManagerRows", taskManagerRows || []);
      this.$set(this.instance, "vmLogs", vmLogs || []);
      this.$set(this.instance, "state", state);
      console.log('updated instance', this.instance);
      this.isFullyLoaded = true;
    },
    async init() {
      await this.loadStorage(this.instance.id);
      await this.loadVmLogsForInstance();
      this.isFullyLoaded = false;
      await this.repeatReloadingTaskStatus();
    },
    async loadStorage(id) {
      await this.$store.dispatch('clientListInstanceParts', {id})
          .then(result => {
            this.listFiles[id] = result;
          })
    },
    async repeatReloadingTaskStatus() {
      console.log('repeatReloadingTaskStatus');
      const $this = this;
      setTimeout(async function() {
        //await $this.loadPipelineTaskForInstance($this.instance.id);
        //await $this.loadVmLogsForInstance();
        await $this.loadTaskManagerAndVMLogsAndInstance();
        if($this.taskIsRunning($this.instance.taskManagerRows[0])) {
          console.log('taskIsRunning', $this.instance.taskManagerRows[0]);
          await $this.loadTaskManagerAndVMLogsAndInstance();
        } else {
          $this.setOriginalPreview();
        }
      }, 8000);
    },
    /*async loadPipelineTaskForInstance(id) {
      const row = await this.$store.dispatch('clientGetPipelineStatus', {id})
          .catch(e => {
            this.$set(this.instance, "taskManagerRows", []);
            if(e.statusCode === 404) {
              this.$set(this.instance, 'state', 'a');
            }
            return null;
          });
      if(row) {
        this.$set(this.instance, "taskManagerRows", [row]);
      }
    },*/
    setOriginalPreview() {
      if(this.previewKey === 'preview.png') {
        return;
      }
      this.previewKey = 'preview.png';
      this.previewReRenderKey += 1;
    },
    setAltPreview() {
      if(this.previewKey === this.altPreviewKey) {
        return;
      }
      this.previewKey = this.altPreviewKey;
      this.previewReRenderKey += 1;
    },
    async openMockupPreviewDialog() {
      this.mockupPreviewDialog = true;
      this.mockupPreviewFiles = [];
      this.mockupPreviewSelectedKey = '';
      this.mockupPreviewLoading = true;
      await this.$store.dispatch('clientListInstanceParts', {id: this.instance.id})
          .then(files => {
            const allowed = ['glb', 'fbx'];
            this.mockupPreviewFiles = (files || []).filter(f => {
              const ext = f.key && f.key.includes('.') ? f.key.split('.').pop().toLowerCase() : '';
              return allowed.includes(ext);
            });
            if (this.mockupPreviewFiles.length) {
              this.mockupPreviewSelectedKey = this.mockupPreviewFiles[0].key;
            }
          })
          .catch(() => {
            this.mockupPreviewFiles = [];
          });
      this.mockupPreviewLoading = false;
    },
    closeMockupPreviewDialog() {
      this.mockupPreviewDialog = false;
      this.mockupPreviewFiles = [];
      this.mockupPreviewSelectedKey = '';
      this.mockupPreviewLoading = false;
    },
    async confirmMockupPreview() {
      if (!this.mockupPreviewSelectedKey) return;
      this.mockupPreviewLoading = true;
      const selectedKeyExt = this.mockupPreviewSelectedKey.includes('.')
          ? this.mockupPreviewSelectedKey.split('.').pop()
          : 'fbx';
      await this.$store.dispatch('createAssetLinkFile', {
        id: this.$route.params.id, // todo: send the assemblyAssetId through props to here.
        linkFileName: `mockup.${selectedKeyExt}`,
        refContainerType: 'instances',
        refContainerId: this.instance.id,
        refKey: this.mockupPreviewSelectedKey,
      }).then(() => {
        this.$store.dispatch('createNotification', {text: this.$t('Mockup preview set successfully')});
        this.closeMockupPreviewDialog();
      }).catch(() => {
        this.$store.dispatch('createNotification', {text: this.$t('Failed to set mockup preview')});
        this.mockupPreviewLoading = false;
      });
    },
  }
}
</script>

<style lang="scss">
.last-profile-instance-field {
  position: relative;
  .three-dot-field {
    position: absolute;
    top: 5px;
    right: 25px;
  }
}
.elevated-z-index {
  z-index: 555;
}
.threedot-name-field {
  position: relative;
  padding-left: 40px;
  .preview-dot-field {
    z-index: 555;
    position: absolute;
    top: 5px;
    left: 5px;
  }
}
</style>

<style lang="scss" scoped>
.preview-image {
  padding-top: 0;
  width: 50px;
  border-radius: 3px;
  border: 1px solid #555;
}
.project-list {
  transition: all 300ms ease;
  a {
    opacity: 0.8;
  }
  a &:hover {
    opacity: 1;
  }
}
.mockup-preview-select option {
  background-color: var(--vform-editor-ui-secondary-color);
  color: #fff;
}
</style>